import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import '../styles/home.css';
import scannerService from '../services/scannerService';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import PixelSpinner from '../components/common/PixelSpinner';

const CAPABILITY_SHORTCUTS = [
  {
    key: 'payload-neutralization',
    title: 'Payload Neutralization',
    description: 'Sanitize all incoming transmissions to block hostile injection attacks. Enforce strict validation parameters at all entry points to ensure SQL, XSS, and command injection vectors are instantly disarmed before reaching the core logic.',
    accent: 'blue'
  },
  {
    key: 'identity-fortification',
    title: 'Identity Fortification',
    description: 'Deploy cryptographic locks to repel unauthorized authentication breaches. Mandate multi-factor verification, implement stringent session timeouts, and establish zero-trust continuous authorization for all personnel.',
    accent: 'green'
  },
  {
    key: 'perimeter-hardening',
    title: 'Perimeter Hardening',
    description: 'Audit and seal tactical blind spots in your web infrastructure. Continuously patch exposed vulnerabilities, enforce strict CORS policies, and minimize the external attack surface to prevent unauthorized reconnaissance.',
    accent: 'coral'
  }
];

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, logout, loading: authLoading } = useAuth();
  const [scanUrl, setScanUrl] = useState('');
  const [isScanActive, setIsScanActive] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  const [recentScans, setRecentScans] = useState([]);
  const [error, setError] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scanMode, setScanMode] = useState('standard'); // 'quick', 'standard', 'deep'
  const [showModeDetails, setShowModeDetails] = useState(false);
  const [currentScanId, setCurrentScanId] = useState(null); // Track current scan
  const [isLoadingScans, setIsLoadingScans] = useState(false);
  const isInitialLoadRef = useRef(true); // Use ref instead of state to avoid re-renders
  const accountMenuRef = useRef(null);

  // Scan mode configurations with detailed feature breakdown
  const scanModes = {
    quick: {
      label: 'Quick',
      duration: '~60 mins',
      description: 'Fast scan with Nuclei and Nikto for rapid vulnerability detection',
      scanners: ['nuclei', 'nikto'],
      deep_scan: false,  // Quick mode uses focused template tags
      include_low_risk: true,
      features: [
        { name: 'CVE Detection', enabled: true, description: 'Known vulnerability scanning' },
        { name: 'Misconfigurations', enabled: true, description: 'Common security misconfigurations' },
        { name: 'Exposed Panels', enabled: true, description: 'Admin panels & dashboards' },
        { name: 'Tech Detection', enabled: true, description: 'Technology fingerprinting' },
        { name: 'SQL Injection', enabled: true, description: 'Database injection attacks' },
        { name: 'XSS Detection', enabled: true, description: 'Cross-site scripting' },
        { name: 'SSRF Detection', enabled: true, description: 'Server-side request forgery' },
        { name: 'RCE Detection', enabled: true, description: 'Remote code execution' },
        { name: 'Server Misconfig', enabled: true, description: 'Web server security issues' },
        { name: 'AI Analysis', enabled: true, description: 'AI-powered insights' },
        { name: 'MITRE Mapping', enabled: true, description: 'ATT&CK technique mapping' }
      ],
      bestFor: 'Fast security checks, CI/CD pipelines, quick assessments'
    },
    standard: {
      label: 'Standard',
      duration: '~150 mins',
      description: 'Comprehensive scan with Nuclei DAST, Wapiti, and Nikto',
      scanners: ['nuclei', 'wapiti', 'nikto'],
      deep_scan: false,  // Standard mode uses balanced scanning depth
      include_low_risk: true,
      features: [
        { name: 'CVE Detection', enabled: true, description: 'Known vulnerability scanning' },
        { name: 'DAST Mode', enabled: true, description: 'Active vulnerability testing' },
        { name: 'Misconfigurations', enabled: true, description: 'Common security misconfigurations' },
        { name: 'Exposed Panels', enabled: true, description: 'Admin panels & dashboards' },
        { name: 'Tech Detection', enabled: true, description: 'Technology fingerprinting' },
        { name: 'SQL Injection', enabled: true, description: 'Database injection attacks' },
        { name: 'XSS Detection', enabled: true, description: 'Cross-site scripting' },
        { name: 'CSRF Detection', enabled: true, description: 'Cross-site request forgery' },
        { name: 'SSRF Detection', enabled: true, description: 'Server-side request forgery' },
        { name: 'Server Misconfig', enabled: true, description: 'Web server security issues' },
        { name: 'Outdated Software', enabled: true, description: 'Version detection & CVEs' },
        { name: 'AI Analysis', enabled: true, description: 'AI-powered insights' },
        { name: 'MITRE Mapping', enabled: true, description: 'ATT&CK technique mapping' }
      ],
      bestFor: 'Regular security assessments, pre-deployment checks, weekly scans'
    },
    deep: {
      label: 'Deep',
      duration: '~300 mins',
      description: 'Maximum coverage with ZAP, Nuclei DAST, Wapiti, and Nikto',
      scanners: ['owasp', 'nuclei', 'wapiti', 'nikto'],
      deep_scan: true,
      include_low_risk: true,
      features: [
        { name: 'CVE Detection', enabled: true, description: 'Known vulnerability scanning' },
        { name: 'DAST Mode', enabled: true, description: 'Active vulnerability testing' },
        { name: 'Misconfigurations', enabled: true, description: 'Common security misconfigurations' },
        { name: 'Exposed Panels', enabled: true, description: 'Admin panels & dashboards' },
        { name: 'Tech Detection', enabled: true, description: 'Technology fingerprinting' },
        { name: 'SQL Injection', enabled: true, description: 'Database injection attacks' },
        { name: 'XSS Detection', enabled: true, description: 'Cross-site scripting' },
        { name: 'OWASP ZAP Active', enabled: true, description: 'Dynamic application testing' },
        { name: 'Spider Crawling', enabled: true, description: 'Deep site exploration' },
        { name: 'AJAX Spider', enabled: true, description: 'JavaScript app crawling' },
        { name: 'Authentication Tests', enabled: true, description: 'Login & session security' },
        { name: 'API Security', enabled: true, description: 'REST/GraphQL endpoint testing' },
        { name: 'XXE Detection', enabled: true, description: 'XML external entity attacks' },
        { name: 'Command Injection', enabled: true, description: 'OS command injection' },
        { name: 'File Inclusion', enabled: true, description: 'LFI/RFI vulnerabilities' },
        { name: 'Server Misconfig', enabled: true, description: 'Web server security issues' },
        { name: 'Outdated Software', enabled: true, description: 'Version detection & CVEs' },
        { name: 'SSL/TLS Issues', enabled: true, description: 'Certificate & cipher checks' },
        { name: 'Default Files', enabled: true, description: 'Dangerous default content' },
        { name: 'AI Analysis', enabled: true, description: 'AI-powered insights' },
        { name: 'MITRE Mapping', enabled: true, description: 'ATT&CK technique mapping' }
      ],
      bestFor: 'Thorough security audits, compliance requirements, production assessments'
    }
  };

  // Define loadRecentScans before useEffect hooks
  const loadRecentScans = useCallback(async (showLoading = true) => {
    try {
      console.log('[Home] Loading recent scans, isAuthenticated:', isAuthenticated);
      if (!isAuthenticated) {
        console.log('[Home] Not authenticated, skipping scan load');
        setRecentScans([]);
        setError(null);
        setIsLoadingScans(false);
        return;
      }

      // Only show loading state if requested (initial load)
      if (showLoading) {
        setIsLoadingScans(true);
      }

      // Check if we have a token
      const token = localStorage.getItem("access_token") || localStorage.getItem("authToken");
      console.log('[Home] Token check - found:', !!token, 'length:', token?.length);

      if (!token) {
        console.warn('[Home] No token found in localStorage despite isAuthenticated being true');
        // Don't set error during background polling to prevent disrupting UX
        if (showLoading) {
          setError('Authentication token not found. Please login again.');
        }
        setRecentScans([]);
        setIsLoadingScans(false);
        return;
      }

      console.log('[Home] Loading recent scans...');
      const response = await scannerService.listScans(0, 20);
      console.log('[Home] Recent scans response:', response);
      setRecentScans(response.scans || []);
      setError(null); // Clear any previous errors on success
    } catch (error) {
      console.error('[Home] Failed to load recent scans:', error);

      // Only update UI with error on initial load, not during polling
      if (showLoading) {
        setRecentScans([]); // Clear scans on error during initial load
        setError('Failed to load scan history: ' + (error.message || 'Unknown error'));
      } else {
        // During background polling, keep existing data but clear error if we had one
        // This prevents stale errors from staying visible
        console.warn('[Home] Background polling failed, keeping existing scan data');
        // Don't show error during background polling
      }
    } finally {
      if (showLoading) {
        setIsLoadingScans(false);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    // Only load recent scans after auth has initialized
    if (authLoading) {
      console.log('[Home] Auth still loading, deferring scan load');
      return;
    }

    if (isAuthenticated) {
      // Clear any previous errors when starting a fresh load
      setError(null);

      // Use the current value of ref to determine if we show loading
      const shouldShowLoading = isInitialLoadRef.current;
      loadRecentScans(shouldShowLoading);

      // Mark initial load as complete
      isInitialLoadRef.current = false;
    } else {
      setRecentScans([]);
      setIsLoadingScans(false);
      setError(null); // Clear errors when not authenticated
    }
  }, [isAuthenticated, authLoading, loadRecentScans]);

  // Poll for scan updates when there are in-progress scans
  useEffect(() => {
    if (!isAuthenticated) return;

    const hasInProgressScans = recentScans.some(scan =>
      scan.status === 'pending' ||
      scan.status === 'running' ||
      scan.status === 'in_progress' ||
      scan.status === 'analyzing'
    );

    if (!hasInProgressScans) return;

    let pollInterval;

    // Start polling after a small delay to let the scan start properly
    const startDelay = setTimeout(() => {
    // Poll every 15 seconds — WebSocket provides real-time updates, this is just a safety net
      pollInterval = setInterval(() => {
        loadRecentScans(false); // Don't show loading during polling
      }, 15000);
    }, 5000); // Wait 5 seconds before starting to poll

    return () => {
      clearTimeout(startDelay);
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isAuthenticated, recentScans, loadRecentScans]);

  // Normalize URL to ensure it has a proper schema
  const normalizeUrl = (url) => {
    let normalizedUrl = url.trim();
    if (!normalizedUrl) return '';

    // Add https:// if no schema is present
    if (!normalizedUrl.match(/^https?:\/\//i)) {
      normalizedUrl = 'https://' + normalizedUrl;
    }
    return normalizedUrl;
  };

  const handleScan = async () => {
    if (!scanUrl.trim()) {
      setError('Please enter a valid URL');
      return;
    }

    // Normalize URL before sending
    const targetUrl = normalizeUrl(scanUrl);

    setIsScanActive(true);
    setError(null);
    setScanProgress(0);
    setCurrentStage('Initializing Tactics...');

    try {
      console.log('Starting scan for URL:', targetUrl);

      // Get scan configuration based on selected mode
      const modeConfig = scanModes[scanMode];

      // Start the scan with mode-specific settings
      const result = await scannerService.startScan(targetUrl, modeConfig.scanners, {
        enable_ai_analysis: true,
        enable_mitre_mapping: true,
        include_low_risk: modeConfig.include_low_risk,
        deep_scan: modeConfig.deep_scan,
        scan_mode: scanMode,
        timeout_minutes: modeConfig.timeout
      });

      console.log('Scan started, response:', result);
      const scanId = result.scan_id;

      if (!scanId) {
        throw new Error('No scan ID returned from backend');
      }

      // Store scan ID for auto-navigation
      setCurrentScanId(scanId);

      // Reload recent scans to show the new scan (without showing loading state)
      loadRecentScans(false).catch((err) => {
        console.error('Failed to reload recent scans after starting new scan:', err);
        // Don't fail the entire scan start just because recent scans failed to load
        // The scan will still proceed normally
      });

      console.log('Setting up WebSocket for scan:', scanId);

      // Create a timeout for WebSocket connection
      let websocketConnected = false;
      let scanCompleted = false;
      let pollingActive = false;
      let reconnectTimer = null;
      let reconnectAttempts = 0;
      const maxReconnectAttempts = 3;

      const getProgressValue = (statusPayload) => {
        if (!statusPayload) return 0;
        if (typeof statusPayload.progress_percent === 'number') {
          return statusPayload.progress_percent;
        }
        if (typeof statusPayload.progress === 'object') {
          return statusPayload.progress?.overall_percent || 0;
        }
        if (typeof statusPayload.progress === 'number') {
          return statusPayload.progress;
        }
        return 0;
      };

      const startPollingIfNeeded = (id) => {
        if (pollingActive || scanCompleted) return;
        pollingActive = true;
        pollScanStatus(id);
      };

      const scheduleWsReconnect = (id) => {
        if (scanCompleted || reconnectAttempts >= maxReconnectAttempts || reconnectTimer) {
          return;
        }

        reconnectAttempts += 1;
        const delay = Math.min(5000 * reconnectAttempts, 15000);
        console.info(`Scheduling WebSocket reconnect attempt ${reconnectAttempts}/${maxReconnectAttempts} in ${delay}ms`);

        reconnectTimer = setTimeout(() => {
          reconnectTimer = null;
          if (scanCompleted) {
            return;
          }
          setupScanWebSocket(id);
        }, delay);
      };

      const wsTimeout = setTimeout(() => {
        if (!websocketConnected && !scanCompleted) {
          console.warn('WebSocket did not connect within 15 seconds, polling for status...');
          startPollingIfNeeded(scanId);
        }
      }, 15000);

      // Polling fallback function
      const pollScanStatus = async (id) => {
        try {
          const status = await scannerService.getScanStatus(id);
          console.log('Polled scan status:', status);
          const scanStatus = (status.scan_status || status.status || '').toLowerCase();

          if (scanStatus === 'completed') {
            scanCompleted = true;
            setScanProgress(100);
            setCurrentStage('Completed');
            setIsScanActive(false);
            navigate(`/scan/${id}`);
          } else if (scanStatus === 'failed' || scanStatus === 'cancelled') {
            setError(`Scan ${scanStatus}`);
            setIsScanActive(false);
          } else {
            const progressValue = getProgressValue(status);
            setScanProgress(progressValue);
            setCurrentStage(status.current_stage || 'Processing');
            // Continue polling
            setTimeout(() => pollScanStatus(id), 3000);
          }
        } catch (err) {
          if (err?.name === "APIError" && err?.statusCode === 404) {
            console.debug("Scan not yet available in database, waiting for next poll...");
            setTimeout(() => pollScanStatus(id), 3000);
            return;
          }
          console.error('Error polling scan status:', err);
          setError('Unable to get scan status. Please check the scan history.');
          setIsScanActive(false);
        }
      };

      const setupScanWebSocket = (id) => scannerService.setupWebSocket(id, {
        onOpen: () => {
          console.log('WebSocket connected for scan:', id);
          websocketConnected = true;
          reconnectAttempts = 0;
          clearTimeout(wsTimeout);
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
        },
        onProgress: (status) => {
          console.log('Scan progress:', status);
          const progressValue = getProgressValue(status);
          setScanProgress(progressValue);
          setCurrentStage(status.current_stage || 'Processing');
        },
        onComplete: (results) => {
          console.log('Scan completed:', results);
          scanCompleted = true;
          websocketConnected = false;
          clearTimeout(wsTimeout);
          if (reconnectTimer) {
            clearTimeout(reconnectTimer);
            reconnectTimer = null;
          }
          setScanProgress(100);
          setCurrentStage('Completed');
          setIsScanActive(false);
          setCurrentScanId(null);
          navigate(`/scan/${id}`);
        },
        onError: (error) => {
          console.error('WebSocket error:', error);
          setError('Connection error during scan: ' + (error?.message || 'socket closed unexpectedly'));
          websocketConnected = false;
          clearTimeout(wsTimeout);
          // Scanner continues server-side even if WS fails; fallback to HTTP polling.
          startPollingIfNeeded(id);
          scheduleWsReconnect(id);
        },
        onClose: () => {
          console.log('WebSocket connection closed for scan:', id);
          websocketConnected = false;
          clearTimeout(wsTimeout);
          if (!scanCompleted) {
            // WebSocket closed without completion - continue via polling.
            startPollingIfNeeded(id);
            scheduleWsReconnect(id);
          }
        }
      });

      // Setup WebSocket for real-time updates (primary channel)
      setupScanWebSocket(scanId);
    } catch (error) {
      console.error('Scan failed:', error);
      setError(error.message || 'Failed to start scan');
      setIsScanActive(false);
      setCurrentScanId(null);
    }
  };

  const handleScanFromHistory = (scanId) => {
    navigate(`/scan/${scanId}`);
  };

  const handleDeleteScan = async (scanId, e) => {
    e.stopPropagation(); // Prevent navigation when clicking delete
    if (window.confirm('Are you sure you want to permanently delete this scan? This action cannot be undone.')) {
      try {
        await scannerService.deleteScan(scanId);
        // Refresh the scan list without showing loading state
        loadRecentScans(false);
      } catch (error) {
        console.error('Failed to delete scan:', error);
        setError('Failed to delete scan: ' + error.message);
      }
    }
  };

  const handleCancelScan = async (scanId, e) => {
    e.stopPropagation(); // Prevent navigation when clicking cancel
    if (window.confirm('Are you sure you want to cancel this running scan?')) {
      try {
        await scannerService.cancelScan(scanId);
        // Refresh the scan list without showing loading state
        loadRecentScans(false);
      } catch (error) {
        console.error('Failed to cancel scan:', error);
        setError('Failed to cancel scan: ' + error.message);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isScanActive) {
      handleScan();
    }
  };

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentClick);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleDocumentClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsMenuOpen(false);
    }
  }, [isAuthenticated]);

  const handleNavigate = (path) => {
    setIsMenuOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    setIsMenuOpen(false);
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  const handleCapabilityNavigate = (path) => {
    if (!isAuthenticated) {
      return;
    }
    navigate(path);
  };

  return (
    <Layout>
      <div className="home">
        {/* Navigation Bar */}
        <nav className="navbar">
          <div className="navbar-logo"></div>
          <ul className="navbar-menu">
            <li className="navbar-menu__item">
              <button
                type="button"
                className="mission-link nav-pill"
                onClick={() => navigate('/mission-file')}
              >
                Mission File
              </button>
            </li>
            {isAuthenticated ? (
              <li className="navbar-menu__item navbar-menu__item--account" ref={accountMenuRef}>
                <button
                  type="button"
                  className="account-trigger nav-pill"
                  aria-haspopup="true"
                  aria-expanded={isMenuOpen}
                  onClick={() => setIsMenuOpen((previous) => !previous)}
                >
                  Account
                </button>
                <div
                  className={`account-dropdown ${isMenuOpen ? 'account-dropdown--open' : ''}`}
                  role="menu"
                >
                  <button
                    type="button"
                    className="account-dropdown__item"
                    onClick={() => handleNavigate('/profile')}
                    role="menuitem"
                  >
                    Profile
                  </button>
                  <hr className="account-dropdown__divider" />
                  <button
                    type="button"
                    className="account-dropdown__item account-dropdown__item--danger"
                    onClick={handleLogout}
                    role="menuitem"
                  >
                    Log out
                  </button>
                </div>
              </li>
            ) : (
              <li className="navbar-menu__item navbar-menu__item--cta">
                <a className="navbar-link--cta nav-pill" href="/login">Login / Register</a>
              </li>
            )}
          </ul>
        </nav>

        {/* Title Section */}
        <section className="title-section">
          <div className="logo-title">
            <img src={logo} alt="LinkLoad Logo" className="home-logo" />
            <h1 className="project-title">LinkLoad</h1>
            <p className="subtitle">Link. Load. Defend. Repeat.</p>
          </div>
        </section>

        {/* Hero Section */}
        <section className="hero-section" id="console">
          <div className="game-console">
            <div className="game-screen">
              <div className="screen-content">
                {isScanActive ? (
                  <div className="scanning-view">
                    <div className={`glitch-text scanning`}>
                      SCANNING IN PROGRESS
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${scanProgress}%` }}></div>
                    </div>
                    <div className="progress-text">
                      <span className="progress-value">{scanProgress}%</span>
                      <span className="progress-stage">{currentStage}</span>
                    </div>
                    <div className="scan-mode-indicator">
                      Mode: {scanModes[scanMode]?.label || 'Standard'}
                    </div>
                  </div>
                ) : (
                  <div className={`glitch-text ${isAuthenticated && scanUrl ? 'ready' : ''}`}>
                    {!isAuthenticated ? 'SIGN IN TO START' : scanUrl ? 'READY TO SCAN' : 'PRESS START SCAN'}
                  </div>
                )}
              </div>
            </div>

            <div className="scan-controls">
              <input
                type="url"
                value={scanUrl}
                onChange={(e) => setScanUrl(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter target URL (e.g., https://example.com)"
                className="scan-input"
                disabled={isScanActive || !isAuthenticated}
              />

              {/* Scan Mode Selector */}
              {isAuthenticated && !isScanActive && (
                <div className="scan-mode-container">
                  <div className="scan-mode-header">
                    <span className="scan-mode-title">Scan Depth</span>
                    <button
                      type="button"
                      className="scan-mode-info-btn"
                      onClick={() => setShowModeDetails(!showModeDetails)}
                      title="Peek scan details"
                    >
                      {showModeDetails ? 'Close' : 'Peek'}
                    </button>
                  </div>

                  <div className="scan-mode-selector">
                    {Object.entries(scanModes).map(([mode, config]) => (
                      <button
                        key={mode}
                        type="button"
                        className={`scan-mode-btn ${scanMode === mode ? 'active' : ''}`}
                        onClick={() => setScanMode(mode)}
                        title={config.description}
                      >
                        <span className="mode-label">{config.label}</span>
                        <span className="mode-time">{config.duration}</span>
                      </button>
                    ))}
                  </div>

                  {/* Expanded Mode Details */}
                  {showModeDetails && (
                    <div className="scan-mode-details">
                      <div className="mode-details-header">
                        <div className="mode-details-badge">{scanModes[scanMode].label}</div>
                        <div className="mode-details-title">
                          <h4>{scanModes[scanMode].label} Scan</h4>
                          <p>{scanModes[scanMode].description}</p>
                        </div>
                      </div>

                      <div className="mode-details-scanners">
                        <span className="scanners-label">Scanners:</span>
                        <div className="scanners-tags">
                          {scanModes[scanMode].scanners.map(scanner => (
                            <span key={scanner} className={`scanner-tag scanner-${scanner}`}>
                              {scanner === 'owasp' ? 'OWASP ZAP' :
                                scanner === 'nuclei' ? 'Nuclei' :
                                  scanner === 'wapiti' ? 'Wapiti' :
                                    scanner === 'nikto' ? 'Nikto' : scanner}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="mode-features-grid">
                        {scanModes[scanMode].features.map((feature, idx) => (
                          <div
                            key={idx}
                            className={`mode-feature ${feature.enabled ? 'enabled' : 'disabled'}`}
                            title={feature.description}
                          >
                            <span className="feature-status">{feature.enabled ? 'YES' : 'NO'}</span>
                            <span className="feature-name">{feature.name}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mode-best-for">
                        <span className="best-for-label">Best for:</span>
                        <span className="best-for-text">{scanModes[scanMode].bestFor}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={handleScan}
                className="scan-button"
                disabled={isScanActive || !scanUrl.trim() || !isAuthenticated}
              >
                {isScanActive ? 'SCANNING...' : 'START SCAN'}
              </button>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>
        </section>

        <section className="capability-section" id="capabilities">
          <div className="capability-header">
            <h2 className="capability-title">Tactical Security Protocols</h2>
            <p className="capability-subtitle">Essential defensive measures for web application fortification.</p>
          </div>
          <div className="capability-grid">
            {CAPABILITY_SHORTCUTS.map((capability) => (
              <div
                key={capability.key}
                className={`capability-card capability-card--${capability.accent}`}
              >
                <span className="capability-window">
                  <span className="capability-window__header">
                    <span className="capability-window__controls">
                      <span className="window-dot" aria-hidden="true"></span>
                      <span className="window-dot" aria-hidden="true"></span>
                      <span className="window-dot" aria-hidden="true"></span>
                    </span>
                    <span className="capability-window__widgets" aria-hidden="true">
                      <span className="window-widget window-widget--min"></span>
                      <span className="window-widget window-widget--max"></span>
                      <span className="window-widget window-widget--close"></span>
                    </span>
                  </span>
                  <span className="capability-window__body">
                    <span className="window-corner window-corner--tl" aria-hidden="true"></span>
                    <span className="window-corner window-corner--tr" aria-hidden="true"></span>
                    <span className="window-corner window-corner--bl" aria-hidden="true"></span>
                    <span className="window-corner window-corner--br" aria-hidden="true"></span>
                    <span className="capability-card__title">{capability.title}</span>
                    <span className="capability-card__description">{capability.description}</span>
                  </span>
                  <span className="capability-window__footer">
                    <span className="capability-card__cta-label">Standard Operating Procedure</span>
                  </span>
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Recent Scans Section - Always visible for authenticated users */}
        {isAuthenticated && (
          <section className="recent-scans-section" id="history">
            <div className="recent-scans-container">
              <h2 className="section-title">Recent Scans</h2>
              {isLoadingScans ? (
                <div className="loading-message" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
                  <PixelSpinner size={32} />
                  <p>Loading scan history...</p>
                </div>
              ) : error ? (
                <div className="error-message-scans">
                  <p>{error}</p>
                </div>
              ) : recentScans.length > 0 ? (
                <div className="recent-scans-list">
                  {recentScans.map((scan) => {
                    const normalizedStatus = String(scan.status || '').toLowerCase();
                    const isInProgress =
                      normalizedStatus === 'queued' ||
                      normalizedStatus === 'pending' ||
                      normalizedStatus === 'running' ||
                      normalizedStatus === 'in_progress' ||
                      normalizedStatus === 'analyzing';
                    const isFailedOrCancelled = normalizedStatus === 'failed' || normalizedStatus === 'cancelled';
                    const isCompleted = normalizedStatus === 'completed';
                    const isClickable = isCompleted;

                    // Handle progress as either object (with overall_percent) or number
                    const progress = typeof scan.progress === 'object'
                      ? (scan.progress?.overall_percent || 0)
                      : (scan.progress || 0);
                    return (
                      <div
                        key={scan.scan_id}
                        className={`recent-scan-item ${isClickable ? '' : 'disabled-click'}`}
                        onClick={() => isClickable && handleScanFromHistory(scan.scan_id)}
                        onKeyDown={(event) => {
                          if (!isClickable) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            handleScanFromHistory(scan.scan_id);
                          }
                        }}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : -1}
                        aria-disabled={!isClickable}
                        title={!isClickable && isFailedOrCancelled ? 'Only completed scans can be opened' : undefined}
                        style={{ cursor: isClickable ? 'pointer' : 'default' }}
                      >
                        <div className="scan-info">
                          <div className="scan-url">{scan.target_url}</div>
                          <div className="scan-meta">
                            {scan.status && (
                              <span className={`status-badge ${scan.status}`}>
                                {scan.status.toUpperCase()}
                              </span>
                            )}
                            {scan.started_at && (
                              <span className="scan-time">
                                {new Date(scan.started_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {isInProgress && (
                            <div className="scan-progress-container">
                              <div className="scan-progress-bar">
                                <div
                                  className="scan-progress-fill"
                                  style={{ width: `${progress}%` }}
                                >
                                  <span className="scan-progress-text">{progress}%</span>
                                </div>
                              </div>
                              <div className="scan-stage-text">{scan.current_stage || 'Processing...'}</div>
                            </div>
                          )}
                        </div>
                        <div className="scan-actions">
                          {isInProgress ? (
                            <button
                              className="cancel-scan-btn"
                              onClick={(e) => handleCancelScan(scan.scan_id, e)}
                              title="Cancel running scan"
                            >
                              CANCEL
                            </button>
                          ) : (
                            <button
                              className="delete-scan-btn"
                              onClick={(e) => handleDeleteScan(scan.scan_id, e)}
                              title="Delete scan permanently"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                <line x1="10" y1="11" x2="10" y2="17"></line>
                                <line x1="14" y1="11" x2="14" y2="17"></line>
                              </svg>
                            </button>
                          )}
                          {isClickable && <div className="scan-arrow"><ArrowRight size={20} strokeWidth={3} /></div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-scans-message">
                  <p>No territory charted yet. Launch your first reconnaissance scan to map the landscape and uncover hidden treasures... or threats.</p>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Features Section */}
        <section className="features-section" id="features">
          <div className="features-header">
            <h4 className="section-title-large">Hack No More</h4>
            <p className="section-subtitle">Link fast. Load smart. Defend stronger. Hackers? Not anymore.</p>
          </div>

          <div className="features-grid">
            <div className="feature-card card-coral">
              <div className="feature-number">01</div>
              <h3>OWASP ZAP</h3>
              <p>Industry-standard dynamic application security testing with comprehensive vulnerability scanning</p>
              <div className="feature-badge">Active</div>
            </div>

            <div className="feature-card card-blue">
              <div className="feature-number">02</div>
              <h3>Nuclei Templates</h3>
              <p>Fast and customizable vulnerability scanner with community-driven templates</p>
              <div className="feature-badge">Active</div>
            </div>

            <div className="feature-card card-green">
              <div className="feature-number">03</div>
              <h3>Wapiti Scanner</h3>
              <p>Web application vulnerability scanner detecting injection flaws and configuration issues</p>
              <div className="feature-badge">Active</div>
            </div>

            <div className="feature-card card-purple">
              <div className="feature-number">04</div>
              <h3>Nikto Scanner</h3>
              <p>Comprehensive web server scanner testing for 6700+ dangerous files and misconfigurations</p>
              <div className="feature-badge">Active</div>
            </div>

            <div className="feature-card card-yellow">
              <div className="feature-number">05</div>
              <h3>AI Analysis</h3>
              <p>LLM-powered vulnerability analysis providing intelligent insights and remediation strategies</p>
              <div className="feature-badge">AI</div>
            </div>

            <div className="feature-card card-pink">
              <div className="feature-number">06</div>
              <h3>MITRE ATT&CK</h3>
              <p>Automated mapping of vulnerabilities to adversary tactics, techniques, and procedures</p>
              <div className="feature-badge">Intel</div>
            </div>

            <div className="feature-card card-coral">
              <div className="feature-number">07</div>
              <h3>Risk Assessment</h3>
              <p>Comprehensive risk scoring with business context and compliance framework alignment</p>
              <div className="feature-badge">Analytics</div>
            </div>
          </div>
        </section>


      </div>
    </Layout>
  );
};

export default Home;
